import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy, Input } from '@angular/core';
import { NgRedux, select } from '@angular-redux/store';
import { MatSort, MatPaginator, MatTableDataSource } from '@angular/material';
import { ApiResponseDispatcher } from '../../../store/api-response/api-response-dispatcher';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { OrchestrationService } from '../../services/orchestration-service/orchestration.service';
import { History } from './models/history.model';
import { SidenavService } from '../../services/sidenav.service';
import { AppState } from '../../../store/state.model';
import { transformDateTime } from '../../../common/helpers/utils';
import { Observable, Subscription as RxSubscription } from 'rxjs';
import { ApiResponseState } from '../../../store/api-response/api-response-state.model';
import { ApiResponseActions } from '../../../store/api-response/api-response-actions';
import { NgIf } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatPaginator as MatPaginator_1 } from '@angular/material/paginator';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatSort as MatSort_1, MatSortHeader } from '@angular/material/sort';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-customer-history',
    templateUrl: './customer-history.component.html',
    styleUrls: ['./customer-history.component.scss'],
    imports: [NgIf, MatButton, MatFormField, MatLabel, MatInput, FormsModule, MatPaginator_1, MatTable, MatSort_1, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, TranslatePipe]
})
export class CustomerHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
  * @Property to hold the displayed columns
  **/
  public showTable = true;
  /**
   * @Property to hold the displayed columns
   **/
  public displayedColumns: string[];
  /**
  * @Property to hold the data source
  **/
  public dataSource: MatTableDataSource<History>;
  /**
   * Total number of customer history records
   */
  public totalCount = 0;
  /**
   * Number of records per page
   */
  public pageSize = 10;
  /**
   * Current page index
   */
  public currentPage = 0;
  /**
   * Customer email for fetching history
   */
  @Input() customerEmail: string;
  /**
   * Flag indicating if customer history response has been received
   */
  public checkCustomerHistoryResponse: boolean;
  /**
   * Error message to display when history fetch fails
   */
  public historyErrorMessage: string = '';
  /**
   * Flag to indicate if there was an error fetching history
   */
  public hasHistoryError: boolean = false;
  /**
   * @arg paginator paginator
   */
  @ViewChild(MatPaginator) paginator: MatPaginator;
  /**
   * @arg sort sort
   */
  @ViewChild(MatSort) sort: MatSort;
  /**
   * Observable for API response state
   */
  @select('api') readonly apiObservable: Observable<ApiResponseState>;
  /**
   * Subscription to the API observable
   */
  private apiSubscription: RxSubscription;
  /**
   * Filter string for table filtering
   */
  public filter: string = '';
  /**
   * Flag to prevent initial paginator event from triggering API call
   */
  private isInitialLoad: boolean = true;
  /**
   * Flag to prevent paginator events during programmatic changes
   */
  private isProgrammaticChange: boolean = false;

  /**
   * @method applyFilter method will take the filterValue and filter over the dataSourse.
   * @param string filterValue anyvalue
   */
  public applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  constructor(
    public orch: OrchestrationService,
    public translate: TranslationService,
    public apiDispatcher: ApiResponseDispatcher,
    private sidenav: SidenavService,
    private ngRedux: NgRedux<AppState>
  ) {  }

  /**
   * @function ngOnInit sets initial configuration
   */
  ngOnInit() {
    this.dataSource = new MatTableDataSource([]);
    
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'eventDate') {
        return item.eventDateRaw;
      }
      return item[property];
    };
    
    this.apiSubscription = this.apiObservable.subscribe((api) => {
      if (api.apiAction === ApiResponseActions.SET_CUSTOMER_HISTORY) {
        const customerHistory = api.customerHistory;

        if (customerHistory && customerHistory['type'] === 'success' && customerHistory['payload']) {
          const payload = customerHistory['payload'];
          const historyData = payload['customerHistory'];
          this.totalCount = payload['totalCount'] || 0;

          this.checkCustomerHistoryResponse = true;
          this.hasHistoryError = false;
          this.historyErrorMessage = '';

          if (historyData && historyData.length > 0) {
            this.setTableData(this.formatTableData(historyData));
          } else {
            this.setTableData([]);
          }
          
          setTimeout(() => {
            this.isInitialLoad = false;
          }, 100);
        } else if (customerHistory && customerHistory['type'] === 'error') {
          this.checkCustomerHistoryResponse = true;
          this.hasHistoryError = true;
          this.totalCount = 0;
          
          const errorPayload = customerHistory['payload'];
          if (errorPayload && errorPayload['status'] === 429) {
            this.historyErrorMessage = this.translate.getCustomTranslation('customer.history.errors.too-many-requests')['value'] 
              || 'Too many requests. Please try again later.';
          } else if (errorPayload && errorPayload['message']) {
            this.historyErrorMessage = errorPayload['message'];
          } else {
            this.historyErrorMessage = this.translate.getCustomTranslation('customer.history.errors.history-fetch-failed')['value']
              || 'Failed to fetch customer history. Please try again.';
          }
          
          this.setTableData([]);
          
          setTimeout(() => {
            this.isInitialLoad = false;
          }, 100);
        }
      }
    });
  }

  /**
   * @function ngAfterViewInit Runs after view is initialized
   */
  ngAfterViewInit() {
    this.initializeSort();
  }

  /**
   * Initializes or re-initializes the sort connection and applies default sort.
   */
  private initializeSort(): void {
    setTimeout(() => {
      if (this.sort && this.dataSource) {
        this.dataSource.sort = this.sort;
        this.sort.disableClear = true;
        
        // Apply default sort to 'eventDescription' column ascending
        this.sort.active = 'eventDescription';
        this.sort.direction = 'asc';
        this.sort.sortChange.emit({ active: 'eventDescription', direction: 'asc' });
      }
    });
  }

  /**
   * @function setTableData set table data
   * @arg
   */
  private setTableData(customerHistory: Array<any>): void {
    this.showTable = customerHistory && customerHistory.length > 0 ? true : false;
    
    const tableSource = this.getTableSource(customerHistory);
    
    this.dataSource.data = tableSource;
    this.displayedColumns = ['eventDescription', 'eventSource', 'eventDate'];
    
    this.initializeSort();
  }

  /**
   * @function formatTableData formats the data for the table
   * @arg data
   * @returns any
   */
  private formatTableData(data): any {
    return data.map(element => {
      return {
        eventDescription: element['eventDescription'],
        eventSource: element['eventSource'],
        eventDate: transformDateTime(element['createdDate']),
        eventDateRaw: new Date(element['createdDate'])
      };
    });
  }
  /**
   * @function getTableSource get table data
   * @arg customerHistory Array<any>
   * @returns Aray<any>
   */
  private getTableSource(customerHistory: Array<any>): Array<any> {
    return customerHistory ? customerHistory : [];
  }
  /**
   * Dispatches the request to fetch customer history from the Orchestration Service.
   */
  private fetchHistory() {
    const email = this.getCustomerEmail();
    const page = this.currentPage + 1;
    const limit = this.pageSize;
    
    if (!email) {
      return;
    }
    
    this.orch.getCustomerHistory(email, page, limit);
  }

  /**
   * Handles pagination changes triggered by user interaction.
   * @param event Pagination event containing pageIndex and pageSize
   */
  public onPageChange(event: any) {
    if (this.isInitialLoad || this.isProgrammaticChange) {
      return;
    }
    
    const pageSizeChanged = this.pageSize !== event.pageSize;
    
    if (pageSizeChanged) {
      this.currentPage = 0;
      this.pageSize = event.pageSize;
      this.isProgrammaticChange = true;
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
      setTimeout(() => {
        this.isProgrammaticChange = false;
      }, 0);
    } else {
      this.currentPage = event.pageIndex;
    }
    
    this.fetchHistory();
  }

  /**
   * Gets the customer email from Redux state
   */
  private getCustomerEmail(): string {
    if (this.customerEmail) {
      return this.customerEmail;
    }
    const state = this.ngRedux.getState();
    if (state.customer && state.customer.data && state.customer.data['emails'] && state.customer.data['emails'].length > 0) {
      return state.customer.data['emails'][0]['emailAddress'];
    }
    return '';
  }

  /**
  * @function close closes the side nav when called
  * @returns void
  **/
  public close(): void {
    this.apiDispatcher.setCustomerHistoryResponse({});
    this.sidenav.close();
    this.reset();
  }

  /**
   * Resets the component state to initial values.
   */
  private reset(): void {
    this.currentPage = 0;
    this.pageSize = 10;
    this.dataSource.data = [];
    this.checkCustomerHistoryResponse = false;
    this.filter = '';
    this.hasHistoryError = false;
    this.historyErrorMessage = '';
    this.isInitialLoad = true;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }

  /**
   * @function ngOnDestroy Runs final sequence
   */
  ngOnDestroy() {
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe();
    }
  }
}
